import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Camera, Eye, Check, X } from 'lucide-react';
// NOTE: adjust this relative path to wherever axiosInstance.js actually sits
// relative to this file.
import axios from "../../../axiosInstance.js";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './KycDocuments.css';

// Documents the user can attach for KYC. None of these are mandatory —
// upload whichever you have, whenever you have it.
const DOCUMENT_FIELDS = [
    { id: 'aadharFront', label: 'Aadhar Card Front' },
    { id: 'aadharBack', label: 'Aadhar Card Back' },
    { id: 'panCard', label: 'PAN Card' },
    { id: 'drivingLicense', label: 'Driving License' },
    { id: 'cancelledCheque', label: 'Cancelled Cheque' },
    { id: 'gst', label: 'GST Certificate' },
];

// customerUpdate's awsLink middleware writes these keys into req.awsObject,
// and the controller picks all of them up into the SQL update:
//   ['GST', 'panCard', 'profileImage', 'adharCard', 'adharCardBack', 'DLCard', 'cancelledCheque', 'agreement']
// so every tile below now maps to a real column.
const BACKEND_FIELD_MAP = {
    aadharFront: 'adharCard',
    aadharBack: 'adharCardBack',
    panCard: 'panCard',
    drivingLicense: 'DLCard',
    cancelledCheque: 'cancelledCheque',
    gst: 'GST',
};

const ENTITY_TYPES = ['Individual', 'Company'];

// A single upload tile. Tapping it (or its camera badge) opens the device
// camera/gallery picker directly via a hidden <input type="file" capture>.
// Once filled, an X button in the corner clears it back to empty so the
// user can capture a different photo.
const DocumentTile = ({ field, previewSrc, registerRef, onTileClick, onFileChange, onDelete }) => {
    const hasFile = Boolean(previewSrc);

    return (
        <div className="kyc-tile-wrap">
            <button
                type="button"
                className={`kyc-tile ${hasFile ? 'has-file' : ''}`}
                onClick={() => onTileClick(field.id)}
            >
                {hasFile ? (
                    <>
                        <img className="kyc-tile-preview" src={previewSrc} alt={field.label} />
                        <span className="kyc-tile-retake">
                            <Camera size={12} />
                        </span>
                        <span
                            className="kyc-tile-delete"
                            role="button"
                            aria-label={`Remove ${field.label}`}
                            onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
                        >
                            <X size={12} />
                        </span>
                    </>
                ) : (
                    <>
                        <span className="kyc-tile-icon">
                            <Camera size={18} />
                        </span>
                        <span className="kyc-tile-cta">Take Photo</span>
                    </>
                )}

                <input
                    ref={(el) => registerRef(field.id, el)}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={(e) => onFileChange(field.id, e)}
                />
            </button>

            <p className="kyc-tile-label">
                {field.label}
                {hasFile && <Check size={12} className="kyc-tile-check" />}
            </p>
        </div>
    );
};

const KycDocuments = () => {
    const navigate = useNavigate();

    const [previews, setPreviews] = useState({});
    const [rawFiles, setRawFiles] = useState({});
    // Tile ids the user explicitly cleared. Needed so a delete with no
    // reupload still tells the backend to null that field out — otherwise
    // "delete" would only be cosmetic until a new photo replaced it.
    const [deletedFields, setDeletedFields] = useState(new Set());
    const [entityType, setEntityType] = useState('Individual');
    const [isEntityMenuOpen, setIsEntityMenuOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingDocs, setIsLoadingDocs] = useState(true);

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');

    // On mount, pull whatever's already saved for this customer and show it
    // in the tiles — otherwise every visit looks blank even after a
    // successful submit, since local state doesn't survive navigation.
    useEffect(() => {
        if (!(userId && token)) {
            setIsLoadingDocs(false);
            return;
        }

        const fetchExistingDocs = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/getCustomerById/${userId}/${userId}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const record = response.data?.data?.[0];
                if (record) {
                    const existingPreviews = {};
                    Object.entries(BACKEND_FIELD_MAP).forEach(([tileId, backendField]) => {
                        if (record[backendField]) {
                            existingPreviews[tileId] = record[backendField];
                        }
                    });
                    setPreviews((prev) => ({ ...existingPreviews, ...prev }));
                }
            } catch (error) {
                console.error('Error fetching existing KYC documents:', error);
            } finally {
                setIsLoadingDocs(false);
            }
        };

        fetchExistingDocs();
    }, [userId, token]);

    // One shared ref bucket, keyed by document id, since hooks can't be
    // created inside the DOCUMENT_FIELDS.map() loop.
    const fileInputRefs = useRef({});

    const registerRef = (id, el) => {
        fileInputRefs.current[id] = el;
    };

    const handleTileClick = (id) => {
        fileInputRefs.current[id]?.click();
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (!file) return;

        setRawFiles((prev) => ({ ...prev, [id]: file }));
        setDeletedFields((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviews((prev) => ({ ...prev, [id]: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleDelete = (id) => {
        setPreviews((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setRawFiles((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setDeletedFields((prev) => new Set(prev).add(id));
    };

    // Whether there's anything new to send: a fresh capture, or a deletion
    // that needs to be persisted. A tile that's simply unchanged from what
    // was already fetched from the server doesn't count.
    const hasChanges = Object.keys(rawFiles).length > 0 || deletedFields.size > 0;

    const handleBack = () => navigate(-1);

    const handleSubmit = async () => {
        if (!hasChanges || isSubmitting) return;

        const formDataObj = new FormData();
        let mappedCount = 0;

        Object.entries(rawFiles).forEach(([id, file]) => {
            const backendField = BACKEND_FIELD_MAP[id];
            if (backendField) {
                formDataObj.append(backendField, file, file.name);
                mappedCount++;
            }
        });

        deletedFields.forEach((id) => {
            if (rawFiles[id]) return; // re-captured after deleting — the file above wins
            const backendField = BACKEND_FIELD_MAP[id];
            if (backendField) {
                formDataObj.append(backendField, '');
                mappedCount++;
            }
        });

        if (mappedCount === 0) {
            toast.error("These documents aren't supported by the update API yet.");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios({
                method: 'PUT',
                url: `${process.env.REACT_APP_BACKEND_URL}/api/customerUpdate/${userId}/${userId}`,
                data: formDataObj,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.status === true) {
                toast.success('Documents submitted successfully!');
                setRawFiles({});
                setDeletedFields(new Set());
            } else {
                toast.error(response.data.message || 'Could not submit documents.');
            }
        } catch (error) {
            console.error('Error submitting KYC documents:', error);
            toast.error('Could not submit documents. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="kyc-page font-satoshi">

            {/* ============ HEADER ============ */}
            <div className="kyc-header">
                <button className="kyc-back-btn" onClick={handleBack} aria-label="Back">
                    <ArrowLeft size={18} />
                </button>

                <div className="kyc-header-text">
                    <h1 className="kyc-title">KYC Documents</h1>
                    <p className="kyc-subtitle">
                        {isLoadingDocs ? 'Loading your saved documents…' : 'Upload the documents below to verify your account'}
                    </p>
                </div>

                <div className="kyc-entity-wrap">
                    <button
                        type="button"
                        className="kyc-entity-pill"
                        onClick={() => setIsEntityMenuOpen((v) => !v)}
                    >
                        {entityType}
                        <ChevronDown size={14} />
                    </button>
                    {isEntityMenuOpen && (
                        <div className="kyc-entity-menu">
                            {ENTITY_TYPES.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`kyc-entity-option ${type === entityType ? 'active' : ''}`}
                                    onClick={() => { setEntityType(type); setIsEntityMenuOpen(false); }}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ============ DOCUMENT GRID ============ */}
            <div className="kyc-content">
                <div className="kyc-card">
                    <p className="kyc-card-title">KYC Documents</p>

                    <div className="kyc-grid">
                        {DOCUMENT_FIELDS.map((field) => (
                            <DocumentTile
                                key={field.id}
                                field={field}
                                previewSrc={previews[field.id]}
                                registerRef={registerRef}
                                onTileClick={handleTileClick}
                                onFileChange={handleFileChange}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                </div>

                <div className="kyc-info-banner">
                    <Eye size={16} className="kyc-info-icon" />
                    <p>None of these are mandatory right now — upload whichever documents you have, and add the rest later.</p>
                </div>
            </div>

            {/* ============ FOOTER ============ */}
            <div className="kyc-footer">
                <button
                    type="button"
                    className="kyc-footer-continue"
                    onClick={handleSubmit}
                    disabled={!hasChanges || isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Documents'}
                </button>
            </div>

            <ToastContainer />
        </div>
    );
};

export default KycDocuments;